import React, { useState } from 'react';
import {
  Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';

// Adds one type to a closed list (pattern_types / category_types). This is the only place a
// type is created: mapping a raw value never does it any more. The dialog shows existing types
// that look like the new name, so "small checkered" is not added next to "checkered" again.
//
// kind: "pattern" | "category"; existing: [{ value, family }]; families: [{ key, label }] (key is
// what the API wants back: the family name for patterns, the family id for categories);
// onCreate(name, familyKey) resolves when the type is saved.
export default function AddTypeDialog({ open, onClose, kind, existing, families, onCreate }) {
  const [name, setName] = useState('');
  const [family, setFamily] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const normalized = name.trim().toLowerCase().replace(/\s+/g, '_');
  const tokens = normalized.split('_').filter(t => t.length >= 4);
  const lookalikes = normalized
    ? existing.filter(t => t.value !== normalized && (
        t.value.includes(normalized) || normalized.includes(t.value) || tokens.some(tok => t.value.includes(tok))
      )).slice(0, 6)
    : [];
  const exists = existing.some(t => t.value === normalized);

  const reset = () => { setName(''); setFamily(''); setError(null); };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onCreate(normalized, family);
      reset();
      onClose();
    } catch (e) {
      setError(e.message || `Failed to add ${kind} type`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>Add {kind} type</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Types are a closed list: the AI can only suggest the types on it, and the matching
          and overlay compare them by name. Add a type only when no existing one covers it. A
          size or strength variant (“small checkered”, “micro dots”) is not a new type.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label={`New ${kind} type`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            helperText={normalized && normalized !== name ? `Saved as “${normalized}”` : 'Lowercase words joined by underscores, e.g. polka_dot'}
            error={exists}
          />
          {exists && <Alert severity="error">“{normalized}” already exists.</Alert>}
          {!exists && lookalikes.length > 0 && (
            <Alert severity="info">
              <Typography variant="body2" sx={{ mb: 1 }}>Existing types that look similar — does one of these already cover it?</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {lookalikes.map(t => <Chip key={t.value} label={`${t.value}${t.family ? ` · ${t.family}` : ''}`} size="small" variant="outlined" />)}
              </Stack>
            </Alert>
          )}
          <FormControl fullWidth>
            <InputLabel>Family</InputLabel>
            <Select value={family} label="Family" onChange={(e) => setFamily(e.target.value)}>
              {families.map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
            </Select>
          </FormControl>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !normalized || exists || !family}>
          {saving ? <CircularProgress size={20} /> : 'Add type'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
